import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, X, MapPin, Clock, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = [
  { id: 'pickup_basketball', title: 'Pickup Basketball', category: 'move', duration: 2, icon: '🏀', image_url: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800&h=500&fit=crop' },
  { id: 'trail_walk', title: 'Trail Walk', category: 'outdoors', duration: 1.5, icon: '🥾', image_url: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=800&h=500&fit=crop' },
  { id: 'coffee_meetup', title: 'Coffee Meetup', category: 'gather', duration: 1.5, icon: '☕', image_url: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&h=500&fit=crop' },
  { id: 'park_hangout', title: 'Park Hangout', category: 'gather', duration: 2, icon: '🌳', image_url: 'https://images.unsplash.com/photo-1526491109649-1f19cee656d0?w=800&h=500&fit=crop' },
  { id: 'run_club', title: 'Run Club', category: 'move', duration: 1, icon: '🏃', image_url: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=800&h=500&fit=crop' },
  { id: 'board_games', title: 'Board Games', category: 'play', duration: 3, icon: '🎲', image_url: 'https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?w=800&h=500&fit=crop' },
  { id: 'volunteer_day', title: 'Volunteer Day', category: 'serve', duration: 3, icon: '🤝', image_url: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=800&h=500&fit=crop' },
  { id: 'yoga_session', title: 'Yoga Session', category: 'move', duration: 1, icon: '🧘', image_url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&h=500&fit=crop' },
  { id: 'open_mic', title: 'Open Mic', category: 'listen', duration: 2.5, icon: '🎤', image_url: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=800&h=500&fit=crop' },
  { id: 'family_outing', title: 'Family Outing', category: 'gather', duration: 2, icon: '👨‍👩‍👧', image_url: 'https://images.unsplash.com/photo-1566454825481-f81e6a2a5855?w=800&h=500&fit=crop' },
  // Community / Help (Pass 5)
  { id: 'neighborhood_cleanup', title: 'Neighborhood Cleanup', category: 'community', duration: 2, icon: '🧹', image_url: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=800&h=500&fit=crop' },
  { id: 'volunteer_help', title: 'Volunteer Help', category: 'community', duration: 3, icon: '🙋', image_url: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&h=500&fit=crop' },
  { id: 'civic_meeting', title: 'Civic Meeting', category: 'community', duration: 1.5, icon: '🏛', image_url: 'https://images.unsplash.com/photo-1529070538774-1843cb3265df?w=800&h=500&fit=crop' },
  { id: 'donation_drive', title: 'Donation Drive', category: 'community', duration: 3, icon: '📦', image_url: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=800&h=500&fit=crop' },
];

// Category → neutral default image (used for "Something else..." or when no template chosen)
const CATEGORY_DEFAULT_IMAGE = {
  move: 'https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?w=800&h=500&fit=crop',
  outdoors: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=800&h=500&fit=crop',
  gather: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&h=500&fit=crop',
  play: 'https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?w=800&h=500&fit=crop',
  serve: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=800&h=500&fit=crop',
  listen: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=800&h=500&fit=crop',
  family: 'https://images.unsplash.com/photo-1566454825481-f81e6a2a5855?w=800&h=500&fit=crop',
  community: 'https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=800&h=500&fit=crop',
};

export default function QuickCreate() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill location from map center if available
  const preLocation = searchParams.get('location') || '';
  const preLat = searchParams.get('lat') || '';
  const preLng = searchParams.get('lng') || '';

  const [form, setForm] = useState({
    location: preLocation,
    lat: preLat,
    lng: preLng,
    template: null,
    title: '',
    category: '',
    duration: 1.5,
    start_date: new Date().toISOString().split('T')[0],
    start_time: '',
    image_url: '',
  });

  const selectTemplate = (tmpl) => {
    setForm(prev => ({
      ...prev,
      template: tmpl.id,
      title: prev.location ? `${tmpl.title} at ${prev.location}` : tmpl.title,
      category: tmpl.category,
      duration: tmpl.duration,
      image_url: tmpl.image_url || CATEGORY_DEFAULT_IMAGE[tmpl.category] || '',
    }));
    setStep(3);
  };

  const handlePublish = async () => {
    if (!user) {
      // Store intent, redirect to auth
      sessionStorage.setItem('quickcreate_pending', JSON.stringify(form));
      navigate('/auth');
      return;
    }

    if (!form.title.trim() || !form.start_date || !form.start_time) {
      toast.error('Add a time to publish');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const startStr = `${form.start_date}T${form.start_time}:00`;
      const startTime = new Date(startStr);
      const endTime = new Date(startTime.getTime() + form.duration * 60 * 60 * 1000);

      const payload = {
        title: form.title,
        category: form.category,
        location: form.location,
        lat: form.lat || null,
        lng: form.lng || null,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        visibility: 'public',
        image_url: form.image_url || CATEGORY_DEFAULT_IMAGE[form.category] || null,
      };

      const res = await axios.post(`${API}/api/activities/`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const actId = res.data.activity_id || res.data.activity?.activity_id;
      navigate(`/activities/${actId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-lg mx-auto px-4 flex items-center justify-between h-14">
          <button onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} className="p-2 rounded-full hover:opacity-70" data-testid="quick-create-back">
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
          </button>
          <span className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Start something</span>
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:opacity-70" data-testid="quick-create-close">
            <X className="w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
          </button>
        </div>
        {/* Progress */}
        <div className="max-w-lg mx-auto px-4 pb-2">
          <div className="flex gap-1">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex-1 h-1 rounded-full" style={{ backgroundColor: s <= step ? 'var(--lk-sage)' : 'var(--fy-border-subtle)' }} />
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">

        {/* STEP 1 — Location */}
        {step === 1 && (
          <div data-testid="step-location">
            <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Where?</h2>
            <p className="text-xs mb-4" style={{ color: 'var(--fy-text-disabled)' }}>Where is this happening?</p>

            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--lk-sage)' }} />
              <input
                type="text"
                value={form.location}
                onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Community Park, Downtown, etc."
                className="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                autoFocus
                data-testid="quick-location-input"
              />
            </div>

            <Button
              onClick={() => setStep(2)}
              disabled={!form.location.trim()}
              className="w-full h-11 rounded-full font-semibold mt-6"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff', opacity: form.location.trim() ? 1 : 0.5 }}
              data-testid="step1-next"
            >
              Next
            </Button>
          </div>
        )}

        {/* STEP 2 — Template */}
        {step === 2 && (
          <div data-testid="step-template">
            <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>What kind?</h2>
            <p className="text-xs mb-4" style={{ color: 'var(--fy-text-disabled)' }}>Pick a template or create your own.</p>

            <div className="grid grid-cols-2 gap-2">
              {TEMPLATES.map(tmpl => (
                <Card
                  key={tmpl.id}
                  className="p-3 cursor-pointer transition-all active:scale-[0.97]"
                  style={{
                    backgroundColor: form.template === tmpl.id ? 'var(--lk-sage-muted, #EDF1E8)' : 'var(--fy-surface-card)',
                    border: `1.5px solid ${form.template === tmpl.id ? 'var(--lk-sage)' : 'var(--fy-border-subtle)'}`,
                  }}
                  onClick={() => selectTemplate(tmpl)}
                  data-testid={`template-${tmpl.id}`}
                >
                  <span className="text-xl mb-1 block">{tmpl.icon}</span>
                  <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>{tmpl.title}</p>
                  <p className="text-[10px]" style={{ color: 'var(--fy-text-disabled)' }}>~{tmpl.duration}h</p>
                </Card>
              ))}
            </div>

            {/* Custom option */}
            <button
              onClick={() => {
                setForm(prev => ({ ...prev, template: 'custom', title: '', category: 'gather', image_url: CATEGORY_DEFAULT_IMAGE['gather'] }));
                setStep(3);
              }}
              className="w-full mt-3 p-3 rounded-xl text-sm font-medium text-left"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-secondary)' }}
              data-testid="template-custom"
            >
              Something else...
            </button>
          </div>
        )}

        {/* STEP 3 — Time + Publish */}
        {step === 3 && (
          <div data-testid="step-time">
            <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>When?</h2>
            <p className="text-xs mb-4" style={{ color: 'var(--fy-text-disabled)' }}>Set a time and you're live.</p>

            {/* Editable title */}
            <div className="mb-4">
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--fy-text-secondary)' }}>Title</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl text-sm outline-none"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                data-testid="quick-title-input"
              />
            </div>

            {/* Cover image — single source of truth for Card / Detail / Flyer */}
            <div className="mb-4">
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--fy-text-secondary)' }}>Cover image</label>
              <div
                className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-2"
                style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
              >
                {form.image_url ? (
                  <img
                    src={form.image_url}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    data-testid="quick-image-preview"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: 'var(--fy-text-disabled)' }}>
                    No cover image
                  </div>
                )}
              </div>
              <input
                type="text"
                value={form.image_url}
                onChange={e => setForm(prev => ({ ...prev, image_url: e.target.value }))}
                placeholder="Paste an image URL (optional)"
                className="w-full px-4 py-2 rounded-xl text-xs outline-none"
                style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                data-testid="quick-image-url-input"
              />
              <p className="text-[10px] mt-1" style={{ color: 'var(--fy-text-disabled)' }}>
                Same image shows on the card, the detail page, and the flyer.
              </p>
            </div>

            {/* Date + Time */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--fy-text-secondary)' }}>Date</label>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={e => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                  data-testid="quick-date-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--fy-text-secondary)' }}>Time</label>
                <input
                  type="time"
                  value={form.start_time}
                  onChange={e => setForm(prev => ({ ...prev, start_time: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-xl text-sm outline-none"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-primary)' }}
                  data-testid="quick-time-input"
                />
              </div>
            </div>

            {/* Summary */}
            <Card className="p-3 mb-5" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
              <p className="text-sm font-medium" style={{ color: 'var(--fy-text-primary)' }}>{form.title || 'Untitled'}</p>
              <div className="flex items-center gap-3 mt-1 text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{form.location}</span>
                {form.start_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{form.start_time}</span>}
              </div>
            </Card>

            {/* Publish */}
            <Button
              onClick={handlePublish}
              disabled={submitting || !form.title.trim() || !form.start_time}
              className="w-full h-12 rounded-full font-bold text-base"
              style={{ backgroundColor: 'var(--lk-sage)', color: '#fff', opacity: (submitting || !form.title.trim() || !form.start_time) ? 0.5 : 1 }}
              data-testid="quick-publish-btn"
            >
              {submitting ? 'Publishing...' : (user ? 'Publish Activity' : 'Sign in to Publish')}
            </Button>

            {!user && (
              <p className="text-xs text-center mt-2" style={{ color: 'var(--fy-text-disabled)' }}>
                You can set everything up first. Sign in is only needed to publish.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
